import React, { useState, useEffect, useMemo } from 'react';
import { ControlPanel, ControlGroup, DataTable, Loader } from '../components/Components';
import './gene.css';
import Tooltip from './Tooltip';
import markerMatrixImage from '../../../../circos.png';

// Searchable Select Component
const SearchableSelect = React.memo(({ options = [], value, onChange, placeholder, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(option =>
            option.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, options]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.searchable-select')) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="searchable-select">
<div className="searchable-select-input" onClick={() => setIsOpen(true)}>
                <input
                    type="text"
                    value={isOpen ? searchTerm : value || ''}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={placeholder}
                    onFocus={() => setIsOpen(true)}
                    className="search-input"
                />
                <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>

            {isOpen && (
                <div className="searchable-select-dropdown">
                    <div className="dropdown-content">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    className={`dropdown-option ${option === value ? 'selected' : ''}`}
                                    onClick={() => handleSelect(option)}
                                >
                                    {option}
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-no-results">No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

// Clean Data Table Component
const CleanDataTable = ({ data, onRowClick, highlightRowKey, highlightRowValue, title, isDynamic = false }) => {
    const [hoveredCell, setHoveredCell] = useState(null);

    if (!data || data.length === 0) {
        return (
            <div className="gene-glass-card" style={{ height: '100%' }}>
                <div className="gene-card-title">
                    {title || 'Data'}
                    <span className="gene-table-count">0 items</span>
                </div>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                    No data available
                </div>
            </div>
        );
    }

    const headers = Object.keys(data[0]);

    const handleCellHover = (e, rowIndex, header, content) => {
        const element = e.target;
        const hasOverflow = element.scrollWidth > element.clientWidth;
        
        if (hasOverflow && content && String(content).trim()) {
            setHoveredCell({
                content: String(content),
                x: e.clientX,
                y: e.clientY,
                visible: true
            });
        }
    };

    const handleCellLeave = () => {
        setHoveredCell(null);
    };

    return (
        <div className="gene-glass-card" style={{ height: '100%' }}>
            <div className="gene-card-title">
                {title || 'Data'}
                <span className="gene-table-count">{data.length} items</span>
            </div>
            <div className={`gene-table-wrapper ${isDynamic ? 'gene-table-dynamic' : ''}`} style={{ maxHeight: 'calc(100%)', overflow: 'auto', position: 'relative' }}>
                <table className="gene-enhanced-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                    <thead>
                        <tr>
                            {headers.map(header => (
                                <th key={header} style={{ padding: '8px 8px' }}>
                                    {header.replace(/_/g, ' ').split(' ').map(word => 
                                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                                    ).join(' ')}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{ height: '2px' }}></tr>
                        {data.map((row, index) => (
                            <tr 
                                key={index}
                                className={highlightRowKey && row[highlightRowKey] === highlightRowValue ? 'highlighted' : ''}
                                onClick={() => onRowClick && onRowClick(row)}
                                style={{ 
                                    cursor: onRowClick ? 'pointer' : 'default',
                                    backgroundColor: highlightRowKey && row[highlightRowKey] === highlightRowValue ? 'rgba(59, 130, 246, 0.1)' : 'transparent'
                                }}
                            >
                                {headers.map(header => (
                                    <td 
                                        key={header} 
                                        className="gene-table-cell"
                                        onMouseEnter={(e) => handleCellHover(e, index, header, row[header])}
                                        onMouseLeave={handleCellLeave}
                                        style={{ padding: '8px 8px' }}
                                    >
                                        {String(row[header] || '')}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Tooltip hoveredCell={hoveredCell} />
        </div>
    );
};

function PredictPage({ 
    selections, 
    handleSelection, 
    options, 
    data, 
    isTabLoading,
    loading 
}) {
    const shouldShowLoading = isTabLoading || loading?.predictShapDeg;

    // Get disease options (filter out healthy control)
    const diseaseOptions = useMemo(() => {
        return (options.predictDiseases || options.diseases?.filter(d => d !== 'healthy')) || [];
    }, [options.predictDiseases, options.diseases]);

    return (
        <div className="gene-page">
            {/* Sidebar */}
            <div className="gene-sidebar">
                <h1 className="gene-sidebar-title">Disease Prediction</h1>
                
                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Disease</p>
                    <SearchableSelect
                        options={diseaseOptions}
                        value={selections.predictDisease}
                        onChange={(value) => handleSelection('predictDisease', value)}
                        placeholder="Search diseases..."
                    />
                </div>

                {/* Disease Abbreviations Legend 
                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Disease Abbreviations</p>
                    <div className="disease-legend">
                        <div className="legend-item"><strong>AS:</strong> Ankylosing Spondylitis</div>
                        <div className="legend-item"><strong>PS:</strong> Parkinsonian syndromes</div>
                        <div className="legend-item"><strong>BD:</strong> Bipolar Disorder</div>
                        <div className="legend-item"><strong>BC:</strong> Breast Cancers</div>
                        <div className="legend-item"><strong>PHD:</strong> Pediatric Hashimoto's Disease</div>
                        <div className="legend-item"><strong>DM:</strong> Diabetes</div>
                        <div className="legend-item"><strong>EP:</strong> Epilepsy</div>
                        <div className="legend-item"><strong>ET:</strong> Essential Tremor</div>
                        <div className="legend-item"><strong>HA:</strong> Habitual Abortion</div>
                        <div className="legend-item"><strong>IGM:</strong> Idiopathic Granulomatous Mastitis</div>
                        <div className="legend-item"><strong>DI:</strong> Drug Intoxication</div>
                        <div className="legend-item"><strong>CAD:</strong> Coronary Artery Disease</div>
                        <div className="legend-item"><strong>CHD:</strong> Congenital Heart Diseases</div>
                        <div className="legend-item"><strong>COPD:</strong> Chronic Obstructive Pulmonary Diseases</div>
                        <div className="legend-item"><strong>LIC:</strong> Liver Cancers</div>
                        <div className="legend-item"><strong>LONG:</strong> Longevity</div>
                        <div className="legend-item"><strong>LUC:</strong> Lung Cancers</div>
                        <div className="legend-item"><strong>MVP:</strong> Mitral Valve Prolapse</div>
                        <div className="legend-item"><strong>PCOS:</strong> Polycystic Ovary Syndrome</div>
                        <div className="legend-item"><strong>RA:</strong> Rheumatoid Arthritis</div>
                        <div className="legend-item"><strong>ARF:</strong> Acute Rheumatic Fever</div>
                        <div className="legend-item"><strong>CC:</strong> Cervical Cancer</div>
                        <div className="legend-item"><strong>GBS:</strong> Gallbladder Stones</div>
                        <div className="legend-item"><strong>SP:</strong> Spontaneous Pneumothorax</div>
                        <div className="legend-item"><strong>SEP:</strong> Sepsis</div>
                        <div className="legend-item"><strong>SCZ:</strong> Schizophrenia</div>
                        <div className="legend-item"><strong>CTRL:</strong> Healthy control</div>
                    </div>
                </div>*/}
            </div>

            {/* Main Content */}
            <div className="gene-main-content">
                {/* Loading State */}
                {shouldShowLoading && (
                    <div className="gene-loading">
                        <div className="gene-loading-spinner"></div>
                        Loading prediction analysis data...
                    </div>
                )}

                {/* Content */}
                {!shouldShowLoading && (
                    <div className="gene-content">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                            {/* Marker Matrix Image */}
                            <div className="gene-glass-card">
                                <div className="gene-card-title">
                                    Marker Matrix Overview
                                </div>
                                <p className="gene-card-subtitle">
                                    Visual representation of marker genes across different conditions
                                </p>
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <img
                                        src={markerMatrixImage}
                                        alt="Marker Matrix"
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            maxHeight: '800px',
                                        }}
                                    />
                                </div>
                            </div>

                            {/* SHAP Importance Table */}
                            {isTabLoading && !data.predictShap ? (
                                <div className="gene-loading">
                                    <div className="gene-loading-spinner"></div>
                                    Loading SHAP data...
                                </div>
                            ) : (
                                <CleanDataTable
                                    data={data.predictShap}
                                    title={`SHAP Importance for ${selections.predictDisease}`}
                                    onRowClick={(row) => handleSelection('shapGene', row.genes)}
                                    highlightRowKey="genes"
                                    highlightRowValue={selections.shapGene}
                                    isDynamic={true}
                                />
                            )}

                            {/* DEG Information Table */}
                            {loading?.predictShapDeg ? (
                                <div className="gene-loading">
                                    <div className="gene-loading-spinner"></div>
                                    Loading DEG information...
                                </div>
                            ) : selections.shapGene ? (
                                <CleanDataTable
                                    data={data.predictShapDeg}
                                    title={`DEG Info for ${selections.shapGene}`}
                                    isDynamic={true}
                                />
                            ) : (
                                <div className="gene-glass-card" style={{ height: '200px' }}>
                                    <div className="gene-card-title">
                                        DEG Information
                                    </div>
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        height: '100%',
                                        flexDirection: 'column',
                                        color: '#6b7280',
                                        gap: '10px',
                                        paddingBottom: '15vh'
                                    }}>
                                        <div style={{ fontSize: '18px' }}>🧬</div>
                                        <div style={{ fontSize: '14px', textAlign: 'center' }}>
                                            <div>Select a gene from the SHAP table above</div>
                                            <div style={{ fontSize: '12px', marginTop: '5px', color: '#9ca3af' }}>
                                                Click any row in the SHAP importance table to view DEG information
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PredictPage;
